import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { fadeUp, stagger } from "./motion";
import { SPECIES, SPECIES_INDEX } from "../data/species";
import { localizeSpecies } from "../data/speciesTranslations";
import { useLang } from "../i18n";

const SpeciesCard = ({ s }) => (
  <motion.div variants={fadeUp} className={s.span}>
    <Link
      to={`/species/${s.slug}`}
      data-testid={`species-card-${s.slug}`}
      className="group relative block h-full overflow-hidden rounded-sm border border-border bg-secondary"
    >
    <div className="absolute inset-0 overflow-hidden">
      <img
        src={s.image}
        alt={s.name}
        className="h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-brand-blue/90 via-brand-blue/25 to-transparent" />
    </div>

    <div className="relative z-10 flex h-full min-h-[340px] flex-col justify-between p-7 md:min-h-[380px]">
      <div className="flex items-start justify-between">
        <span className="rounded-full border border-brand-bone/40 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-brand-bone backdrop-blur-sm">
          {s.tag}
        </span>
        <ArrowUpRight className="text-brand-bone transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" size={22} />
      </div>

      <div>
        <p className="mb-2 font-mono text-[11px] uppercase tracking-[0.2em] text-brand-bone/70">
          {s.origin}
        </p>
        <h3 className="font-serif text-4xl font-medium leading-none text-brand-bone md:text-5xl">
          {s.name}
        </h3>
        <p className="mt-1 font-mono text-xs italic text-brand-bone/60">{s.latin}</p>
        <p className="mt-4 max-w-md text-sm font-light leading-relaxed text-brand-bone/85 opacity-0 transition-opacity duration-500 group-hover:opacity-100">
          {s.note}
        </p>
      </div>
    </div>
    </Link>
  </motion.div>
);

export const SpeciesCatalog = () => {
  const { t, lang } = useLang();
  return (
    <section
      id="species"
      data-testid="species-catalog"
      className="mx-auto max-w-[1600px] px-6 py-28 md:px-12 md:py-40"
    >
      <motion.div
        variants={stagger}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-100px" }}
        className="mb-16 flex flex-col justify-between gap-8 md:flex-row md:items-end"
      >
        <div>
          <motion.span
            variants={fadeUp}
            className="font-mono text-xs uppercase tracking-[0.25em] text-brand-timber"
          >
            {t("catalog.kicker")}
          </motion.span>
          <motion.h2
            variants={fadeUp}
            className="mt-4 max-w-2xl font-serif text-5xl font-medium leading-[0.95] tracking-tight text-brand-blue md:text-6xl"
          >
            {t("catalog.title")}
          </motion.h2>
        </div>
        <motion.p
          variants={fadeUp}
          className="max-w-sm font-light text-base leading-relaxed text-brand-slate"
        >
          {t("catalog.sub")}
        </motion.p>
      </motion.div>

      <motion.div
        variants={stagger}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-80px" }}
        className="grid grid-cols-1 gap-4 lg:grid-cols-12"
      >
        {SPECIES.map((s) => (
          <SpeciesCard key={s.name} s={{ ...localizeSpecies(s, lang), span: s.span }} />
        ))}
      </motion.div>

      {/* Full species index */}
      <motion.div
        variants={stagger}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-80px" }}
        className="mt-20 grid grid-cols-1 gap-x-16 gap-y-12 border-t border-border pt-14 md:grid-cols-2"
      >
        {Object.entries(SPECIES_INDEX).map(([group, items]) => (
          <motion.div key={group} variants={fadeUp} data-testid={`species-index-${group.toLowerCase()}`}>
            <h4 className="mb-6 font-mono text-xs uppercase tracking-[0.25em] text-brand-timber">
              {t(`catalog.${group}`)}
            </h4>
            <ul className="flex flex-wrap gap-x-6 gap-y-3">
              {items.map((sp) => (
                <li key={sp.slug}>
                  <Link
                    to={`/species/${sp.slug}`}
                    data-testid={`species-index-link-${sp.slug}`}
                    className="border-b border-transparent font-serif text-2xl text-brand-blue transition-colors duration-300 hover:border-brand-timber hover:text-brand-timber md:text-3xl"
                  >
                    {sp.name}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>
        ))}
      </motion.div>
    </section>
  );
};
